// Updated: LabeledInvoiceImage.tsx
import React, { useEffect, useRef, useState } from 'react';

interface BoundingBox {
    x: number;
    y: number;
    w: number;
    h: number;
}

interface SourceInfo {
    pageNumber?: number;
    offset?: number;
    length?: number;
    boundingBox?: BoundingBox;
    source?: string;
}

interface FieldCoordinates {
    text: string;
    source?: SourceInfo | string;
    content?: string;
    role?: string;
    [key: string]: any;
}

interface LabeledInvoiceImageProps {
    imgUrl: string;
    fields: FieldCoordinates[];
}

interface ImageDimensions {
    width: number;
    height: number;
    displayWidth?: number;
    displayHeight?: number;
}

// List of common invoice field keywords to identify field types
const FIELD_KEYWORDS = {
    'Invoice Number': ['invoice number', 'invoice no', 'invoice id'],
    'Invoice Date': ['invoice date', 'date'],
    'Due Date': ['due date', 'payment due'],
    'Vendor Name': ['vendor', 'company name', 'from', 'seller'],
    'Vendor Address': ['vendor address', 'address', 'location'],
    'Customer Name': ['customer', 'bill to', 'sold to', 'buyer'],
    'Customer Address': ['customer address', 'bill to address'],
    'Amount': ['amount', 'total', 'grand total', 'sum'],
    'Tax': ['tax', 'vat', 'gst'],
    'Description': ['description', 'item', 'product'],
    'Quantity': ['quantity', 'qty', 'qnt'],
    'Unit Price': ['price', 'unit price', 'rate'],
};

// Sensitive field patterns for masking
const SENSITIVE_PATTERNS = {
    address: ['address', 'street', 'city', 'state', 'zip', 'postal', 'location'],
    phone: ['phone', 'telephone', 'mobile', 'contact number'],
    taxId: ['tax id', 'tax number', 'ein', 'ssn', 'vat id', 'pan', 'gstin'],
    email: ['email', 'contact'],
    bankAccount: ['account number', 'account', 'routing number', 'iban', 'swift'],
};

const isSensitiveField = (fieldLabel: string, fieldContent: string): boolean => {
    const lowerLabel = fieldLabel.toLowerCase();
    const lowerContent = fieldContent.toLowerCase();

    for (const [, patterns] of Object.entries(SENSITIVE_PATTERNS)) {
        if (patterns.some(pattern => lowerLabel.includes(pattern) || lowerContent.includes(pattern))) {
            return true;
        }
    }
    return false;
};

const maskSensitiveContent = (content: string, fieldLabel: string): string => {
    const lowerLabel = fieldLabel.toLowerCase();

    // Detect field type and apply appropriate masking
    if (SENSITIVE_PATTERNS.address.some(p => lowerLabel.includes(p))) {
        return '[ADDRESS HIDDEN]';
    }
    if (SENSITIVE_PATTERNS.phone.some(p => lowerLabel.includes(p))) {
        // Keep last 4 digits if available
        const match = content.match(/\d+$/);
        return match ? `***-***-${match[0]}` : '[PHONE HIDDEN]';
    }
    if (SENSITIVE_PATTERNS.taxId.some(p => lowerLabel.includes(p))) {
        // Keep last 4 digits
        const match = content.match(/\d+$/);
        return match ? `XXX-XX-${match[0]}` : '[TAX ID HIDDEN]';
    }
    if (SENSITIVE_PATTERNS.email.some(p => lowerLabel.includes(p))) {
        // Show first character and domain
        const emailMatch = content.match(/^(.).*@(.+)$/);
        return emailMatch ? `${emailMatch[1]}***@${emailMatch[2]}` : '[EMAIL HIDDEN]';
    }
    if (SENSITIVE_PATTERNS.bankAccount.some(p => lowerLabel.includes(p))) {
        // Keep last 4 digits
        const match = content.match(/\d+$/);
        return match ? `****${match[0]}` : '[ACCOUNT HIDDEN]';
    }

    return content;
};

const getFieldLabel = (text: string | undefined, index: number): string => {
    if (!text) return `Field ${index + 1}`;

    const lowerText = text.toLowerCase().substring(0, 50);

    for (const [label, keywords] of Object.entries(FIELD_KEYWORDS)) {
        if (keywords.some(keyword => lowerText.includes(keyword))) {
            return label;
        }
    }

    return text.substring(0, 30);
};

// Parse bounding box from various formats
const parseBoundingBox = (source: any) => {
    if (!source) return null;

    // Handle BoundingBox object format (x, y, w, h)
    if (typeof source === 'object' && 'x' in source && 'y' in source && 'w' in source && 'h' in source) {
        const { x = 0, y = 0, w = 0, h = 0 } = source;
        return {
            points: [
                { x: x, y: y },           // Top-left
                { x: x + w, y: y },       // Top-right
                { x: x + w, y: y + h },   // Bottom-right
                { x: x, y: y + h }        // Bottom-left
            ]
        };
    }

    // Handle source string format "D(page,x1,y1,x2,y2,x3,y3,x4,y4)"
    if (typeof source === 'string') {
        const match = source.match(/D\((\d+),([\d,]+)\)/);
        if (match) {
            const coords = match[2].split(',').map(Number);
            if (coords.length === 8) {
                return {
                    page: parseInt(match[1]),
                    points: [
                        { x: coords[0], y: coords[1] },
                        { x: coords[2], y: coords[3] },
                        { x: coords[4], y: coords[5] },
                        { x: coords[6], y: coords[7] }
                    ]
                };
            }
        }
    }

    return null;
};

// Calculate bounding box styles with scaling
const getBoundingStyle = (bbox: any, imageDimensions: ImageDimensions) => {
    if (!bbox || !imageDimensions.width || !bbox.points) return null;

    const scaleX = (imageDimensions.displayWidth || imageDimensions.width) / imageDimensions.width;
    const scaleY = (imageDimensions.displayHeight || imageDimensions.height) / imageDimensions.height;

    const xCoords = bbox.points.map((p: any) => p.x * scaleX);
    const yCoords = bbox.points.map((p: any) => p.y * scaleY);

    const left = Math.min(...xCoords);
    const top = Math.min(...yCoords);
    const right = Math.max(...xCoords);
    const bottom = Math.max(...yCoords);

    const width = right - left;
    const height = bottom - top;

    const polygonPoints = bbox.points.map((p: any) =>
        `${(p.x * scaleX) - left}px ${(p.y * scaleY) - top}px`
    ).join(', ');

    return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        clipPath: `polygon(${polygonPoints})`
    };
};

export const LabeledInvoiceImage: React.FC<LabeledInvoiceImageProps> = ({ imgUrl, fields }) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);

    const [selectedField, setSelectedField] = useState<number | null>(null);
    const [hoveredField, setHoveredField] = useState<number | null>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageDimensions, setImageDimensions] = useState<ImageDimensions>({
        width: 0,
        height: 0,
        displayWidth: 0,
        displayHeight: 0
    });

    // Reset selected field when fields change (e.g., invoice changes)
    useEffect(() => {
        setSelectedField(null);
    }, [fields]);

    useEffect(() => {
        if (imageRef.current && imageLoaded) {
            const rect = imageRef.current.getBoundingClientRect();
            setImageDimensions({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight,
                displayWidth: rect.width,
                displayHeight: rect.height
            });
        }
    }, [imageLoaded]);

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    const handleFieldClick = (index: number) => {
        setSelectedField(selectedField === index ? null : index);
        const fieldElement = document.getElementById(`field-${index}`);
        if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleFieldHover = (index: number | null) => {
        setHoveredField(index);
    };

    // Color palette for different fields
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];

    const getHighlightColor = (index: number) => colors[index % colors.length];

    return (
        <div style={{ display: 'flex', gap: '20px', width: '100%', padding: '20px' }}>
            {/* Left Side - Invoice Image with Highlights */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ marginBottom: '15px' }}>
                    <h3 style={{ marginBottom: '5px', fontSize: '18px', fontWeight: 'bold' }}>📋 Original Invoice</h3>
                    <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                        {selectedField !== null
                            ? `✓ Highlighting: ${getFieldLabel(fields[selectedField]?.text || fields[selectedField]?.content, selectedField)}`
                            : '👆 Click fields to highlight them'}
                    </p>
                </div>

                <div
                    ref={imageContainerRef}
                    style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%',
                        border: '2px solid #ddd',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    <img
                        ref={imageRef}
                        src={imgUrl}
                        alt="Invoice"
                        style={{
                            display: 'block',
                            width: '100%',
                            height: 'auto'
                        }}
                        onLoad={handleImageLoad}
                    />

                    {/* Highlight overlays with numbers */}
                    {imageLoaded && fields.map((field, index) => {
                        const source = field.source || field;
                        const bbox = parseBoundingBox(source);
                        const style = getBoundingStyle(bbox, imageDimensions);
                        const isSelected = selectedField === index;
                        const isHovered = hoveredField === index;
                        const color = getHighlightColor(index);

                        if (!style) return null;

                        return (
                            <div
                                key={index}
                                style={{
                                    ...style,
                                    position: 'absolute',
                                    cursor: 'pointer',
                                    border: `3px solid ${color}`,
                                    backgroundColor: isSelected || isHovered ? `${color}40` : `${color}20`,
                                    transition: 'all 0.2s ease',
                                    zIndex: isSelected ? 10 : isHovered ? 5 : 1,
                                    boxShadow: isSelected ? `0 0 10px ${color}80` : 'none'
                                }}
                                onClick={() => handleFieldClick(index)}
                                onMouseEnter={() => handleFieldHover(index)}
                                onMouseLeave={() => handleFieldHover(null)}
                                title={`${getFieldLabel(field?.text || field?.content, index)}: ${field?.text || field?.content || ''}`}
                            >
                                {/* Field number badge */}
                                {/* <div
                                    style={{
                                        position: 'absolute',
                                        top: '-14px',
                                        left: '-2px',
                                        backgroundColor: color,
                                        color: 'white',
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        border: '2px solid white',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    {index + 1}
                                </div> */}

                                {/* Field label on selection */}
                                {isSelected && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '-32px',
                                            left: '30px',
                                            backgroundColor: color,
                                            color: 'white',
                                            padding: '4px 12px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            whiteSpace: 'nowrap',
                                            zIndex: 20,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                    >
                                        {getFieldLabel(field?.text || field?.content, index)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Right Side - Fields List */}
            <div style={{ flex: 1, minWidth: '0', height: '762px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '15px' }}>
                    <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: 'bold' }}>🔍 Extracted Fields</h3>
                    <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: '600'
                            }}
                        >
                            {fields.length} fields detected
                        </span>
                    </p>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        flex: 1,
                        overflowY: 'auto',
                        paddingRight: '8px'
                    }}
                >
                    {fields?.map((field, index) => {
                        const source = field.source || field;
                        const bbox = parseBoundingBox(source);
                        const color = getHighlightColor(index);
                        const isSelected = selectedField === index;
                        const hasCoordinates = !!bbox;
                        const fieldLabel = getFieldLabel(field?.text || field?.content, index);
                        const originalContent = field?.text || field?.content || 'No content';
                        const isSensitive = isSensitiveField(fieldLabel, originalContent);
                        const displayContent = isSensitive ? maskSensitiveContent(originalContent, fieldLabel) : originalContent;

                        return (
                            <button
                                key={index}
                                id={`field-${index}`}
                                onClick={() => handleFieldClick(index)}
                                onMouseEnter={() => handleFieldHover(index)}
                                onMouseLeave={() => handleFieldHover(null)}
                                style={{
                                    padding: '12px',
                                    border: `2px solid ${isSelected ? color : '#e0e0e0'}`,
                                    backgroundColor: isSelected ? `${color}15` : '#fff',
                                    borderRadius: '8px',
                                    cursor: hasCoordinates ? 'pointer' : 'not-allowed',
                                    opacity: hasCoordinates ? 1 : 0.6,
                                    transition: 'all 0.2s ease',
                                    textAlign: 'left',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    boxShadow: isSelected ? `0 4px 12px ${color}30` : 'none'
                                }}
                                disabled={!hasCoordinates}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        fontSize: '13px',
                                        fontWeight: '600'
                                    }}
                                >
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '26px',
                                            height: '26px',
                                            backgroundColor: color,
                                            color: 'white',
                                            borderRadius: '50%',
                                            fontSize: '11px',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                        }}
                                    >
                                        {index + 1}
                                    </span>
                                    <span style={{ flex: 1, color: '#333' }}>{fieldLabel}</span>
                                    {isSensitive && (
                                        <span style={{ marginLeft: 'auto', fontSize: '14px' }} title="Sensitive field - content masked">🔒</span>
                                    )}
                                    {!hasCoordinates && (
                                        <span style={{ marginLeft: 'auto', fontSize: '14px' }} title="No coordinates">⚠️</span>
                                    )}
                                </div>

                                <div
                                    style={{
                                        fontSize: '12px',
                                        color: isSensitive ? '#d9534f' : '#555',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '100%',
                                        paddingLeft: '36px',
                                        fontWeight: isSensitive ? '600' : '400'
                                    }}
                                >
                                    {displayContent}
                                </div>

                                {field.role && (
                                    <div
                                        style={{
                                            fontSize: '10px',
                                            color: '#fff',
                                            backgroundColor: '#999',
                                            padding: '2px 8px',
                                            borderRadius: '3px',
                                            width: 'fit-content',
                                            marginLeft: '36px'
                                        }}
                                    >
                                        {field.role}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {fields.length > 0 && (
                    <button
                        onClick={() => setSelectedField(null)}
                        disabled={selectedField === null}
                        style={{
                            marginTop: '12px',
                            width: '100%',
                            padding: '12px',
                            backgroundColor: selectedField !== null ? '#ff6b6b' : '#f0f0f0',
                            color: selectedField !== null ? 'white' : '#999',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: selectedField !== null ? 'pointer' : 'not-allowed',
                            fontSize: '13px',
                            fontWeight: '600',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Clear Selection
                    </button>
                )}
            </div>
        </div>
    );
};